import { useState, useEffect, useRef } from "react";
import { X, Sparkles, Clock, Shield, Star, ArrowRight, Tag, BrainCircuit } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLocation } from "@/lib/locations";
import { useLocation as useRouterLocation } from "react-router-dom";
import { cn } from "@/lib/utils";

const BOOKING_URL = "/book";
const STORAGE_KEY = "exit-intent-dismissed";

export const ExitIntentPopup = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [hasTriggered, setHasTriggered] = useState(false);
  const [timeLeft, setTimeLeft] = useState(600);
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const location = useLocation();
  const routerLocation = useRouterLocation();

  const isEs = routerLocation.pathname.startsWith('/es');
  const isRu = routerLocation.pathname.startsWith('/ru');
  const isZh = routerLocation.pathname.startsWith('/zh');
  const isPricingPage = routerLocation.pathname.includes('/pricing') || routerLocation.pathname.includes('/precios');

  const quizTitle = isZh ? "不确定哪个套餐适合您？" : isRu ? "Не уверены, какой план вам подходит?" : isEs ? "¿No estás seguro de qué plan es el adecuado para ti?" : "Not Sure Which Plan Is Right For You?";
  const quizDesc = isZh ? "别空手离开！参加我们快速的AI测验，为您的家庭和预算找到完美的清洁套餐。" : isRu ? "Не уходите с пустыми руками! Пройдите наш быстрый ИИ-тест, чтобы найти идеальный пакет уборки для вашего дома и бюджета." : isEs ? "¡No te vayas con las manos vacías! Toma nuestro rápido cuestionario impulsado por IA para encontrar el paquete de limpieza perfecto para tu hogar y presupuesto." : "Don't leave empty-handed! Take our quick AI-powered quiz to find the perfect cleaning package for your home and budget.";
  const quizBtn = isZh ? "参加快速测验" : isRu ? "Пройти быстрый тест" : isEs ? "Tomar el Cuestionario Rápido" : "Take the Quick Quiz";
  const quizNoThanks = isZh ? "不，谢谢，我以后再看" : isRu ? "Нет, спасибо, я разберусь позже" : isEs ? "No gracias, lo veré más tarde" : "No thanks, I'll figure it out later";

  const textExclusive = isZh ? `专属优惠 — ${location.name}` : isRu ? `Эксклюзивное предложение — ${location.name}` : isEs ? `Oferta Exclusiva — ${location.name}` : `Exclusive Offer — ${location.name}`;
  const textWait = isZh ? "等等 — 请别离开" : isRu ? "Подождите — не уходите" : isEs ? "Espera — No te vayas" : "Wait — Don't Leave";
  const textSave = isZh ? "今日节省 10%" : isRu ? "Сэкономьте 10% сегодня" : isEs ? "Ahorra 10% Hoy" : "Save 10% Today";
  const textDesc = isZh ? `获得首次专业家庭清洁折扣。深受 ${location.name} 数百名房主的信任。` : isRu ? `Получите скидку на первую профессиональную уборку дома. Нам доверяют сотни домовладельцев в ${location.name}.` : isEs ? `Obtén un descuento en tu primera limpieza profesional. Confiado por cientos de propietarios en ${location.name}.` : `Get your first professional home cleaning at a discount. Trusted by hundreds of ${location.name} homeowners.`;
  const textExpires = isZh ? "优惠到期" : isRu ? "Предложение истекает через" : isEs ? "La oferta expira en" : "Offer expires in";
  const textPromo = isZh ? "你的优惠码" : isRu ? "Ваш промокод" : isEs ? "Tu código promocional" : "Your promo code";
  const textCopied = isZh ? "已复制！" : isRu ? "Скопировано!" : isEs ? "¡Copiado!" : "Copied!";
  const textTapToCopy = isZh ? "点击复制" : isRu ? "Нажмите, чтобы скопировать" : isEs ? "Toca para copiar" : "Tap to copy";
  const textClaim = isZh ? "领取我的 10% 折扣 — 立即预约" : isRu ? "Получить скидку 10% — Записаться" : isEs ? "Reclamar mi 10% — Reservar Ahora" : "Claim My 10% Off — Book Now";
  const textNoThanks = isZh ? "不，谢谢，我愿意付全款" : isRu ? "Нет, спасибо, я заплачу полную стоимость" : isEs ? "No gracias, pagaré el precio completo" : "No thanks, I'll pay full price";
  
  const textGuarantee = isZh ? "200% 保证" : isRu ? "Гарантия 200%" : isEs ? "Garantía del 200%" : "200% Guarantee";
  const textRated = isZh ? "4.9★ 评分" : isRu ? "Оценка 4.9★" : isEs ? "Calificación 4.9★" : "4.9★ Rated";
  const textInsured = isZh ? "保险和担保" : isRu ? "Застраховано" : isEs ? "Asegurado y Afianzado" : "Insured & Bonded";

  const textNoCC = isZh ? "✓ 无需信用卡" : isRu ? "✓ Кредитная карта не требуется" : isEs ? "✓ No se requiere tarjeta" : "✓ No credit card required";
  const textCancel = isZh ? "✓ 随时取消" : isRu ? "✓ Отмена в любое время" : isEs ? "✓ Cancela en cualquier momento" : "✓ Cancel anytime";
  const textNewCust = isZh ? "✓ 仅限新客户" : isRu ? "✓ Только для новых клиентов" : isEs ? "✓ Solo clientes nuevos" : "✓ New customers only";

  useEffect(() => {
    const dismissed = sessionStorage.getItem(STORAGE_KEY);
    if (dismissed) setHasTriggered(true);
  }, []);

  // Desktop: mouse leaves viewport top — only wire up after 10s to avoid false triggers
  useEffect(() => {
    if (hasTriggered) return;
    let armed = false;
    const armTimer = setTimeout(() => { armed = true; }, 3000);

    const handleMouseLeave = (e: MouseEvent) => {
      if (armed && e.clientY <= 5 && !hasTriggered) {
        triggerPopup();
      }
    };

    document.addEventListener("mouseleave", handleMouseLeave);
    return () => {
      clearTimeout(armTimer);
      document.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, [hasTriggered]);

  // Mobile: rapid scroll up after 50% page depth
  useEffect(() => {
    if (hasTriggered) return;
    let lastScrollY = window.scrollY;
    let lastScrollTime = Date.now();

    const handleScroll = () => {
      const currentY = window.scrollY;
      const currentTime = Date.now();
      const dt = currentTime - lastScrollTime;
      if (dt === 0) return;
      const velocity = (lastScrollY - currentY) / dt;

      if (velocity > 4.0 && currentY > window.innerHeight * 0.5 && !hasTriggered) {
        triggerPopup();
      }

      lastScrollY = currentY;
      lastScrollTime = currentTime;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [hasTriggered]);

  const triggerPopup = () => {
    setIsVisible(true);
    setHasTriggered(true);
    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) { clearInterval(timerRef.current!); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const handleClose = () => {
    setIsVisible(false);
    sessionStorage.setItem(STORAGE_KEY, "true");
    if (timerRef.current) clearInterval(timerRef.current);
  };

  const handleClaim = () => {
    window.open(BOOKING_URL, "_blank", "noopener,noreferrer");
    handleClose();
  };

  const handleCopyCode = () => {
    navigator.clipboard.writeText("UPLEVEL10").catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const formatTime = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  if (!isVisible) return null;

  if (isPricingPage) {
    return (
      <div
        className="fixed inset-0 z-[200] flex items-center justify-center p-4"
        role="dialog"
        aria-modal="true"
        aria-label="Take our quiz"
      >
        <div
          className="absolute inset-0 bg-black/70 backdrop-blur-sm animate-in fade-in duration-300"
          onClick={handleClose}
        />
        <div className="relative w-full max-w-lg animate-in zoom-in-95 fade-in duration-400 slide-in-from-bottom-4">
          <div className="absolute -inset-1 rounded-3xl bg-gradient-to-r from-primary via-primary/60 to-primary opacity-40 blur-xl" />
          <div className="relative rounded-3xl overflow-hidden border border-primary/20 shadow-2xl bg-background text-center p-6 md:p-10">
            <button
              onClick={handleClose}
              className="absolute right-4 top-4 z-10 p-1.5 rounded-full bg-muted/60 text-muted-foreground hover:text-foreground hover:bg-muted transition-all"
              aria-label="Close popup"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
            
            <div className="flex justify-center mb-6">
              <div className="p-4 bg-primary/10 rounded-full">
                <BrainCircuit className="h-10 w-10 text-primary" />
              </div>
            </div>
            
            <h2 className="text-2xl md:text-3xl font-extrabold tracking-tight text-foreground mb-4">
              {quizTitle}
            </h2>
            <p className="text-muted-foreground text-base mb-8 max-w-sm mx-auto">
              {quizDesc}
            </p>
            
            <div className="space-y-3">
              <Button
                onClick={() => {
                  window.location.href = "/quiz";
                  handleClose();
                }}
                className="w-full h-14 text-lg font-bold bg-primary text-primary-foreground hover:bg-primary/90 rounded-xl shadow-lg shadow-primary/25 btn-pulse group"
              >
                {quizBtn}
                <ArrowRight className="h-5 w-5 ml-2 transition-transform group-hover:translate-x-1" />
              </Button>
              <button
                onClick={handleClose}
                className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors py-2 underline underline-offset-4"
              >
                {quizNoThanks}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Special offer"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm animate-in fade-in duration-300"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-lg animate-in zoom-in-95 fade-in duration-400 slide-in-from-bottom-4">
        <div className="absolute -inset-1 rounded-3xl bg-gradient-to-r from-primary via-primary/60 to-primary opacity-40 blur-xl" />

        <div className="relative rounded-3xl overflow-hidden border border-primary/20 shadow-2xl bg-background">
          <div className="h-1.5 w-full bg-gradient-to-r from-primary via-sky-400 to-primary bg-[length:200%_100%] animate-[shimmer_2s_linear_infinite]" />

          <button
            onClick={handleClose}
            className="absolute right-4 top-4 z-10 p-1.5 rounded-full bg-muted/60 text-muted-foreground hover:text-foreground hover:bg-muted transition-all"
            aria-label="Close offer"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>

          <div className="p-6 md:p-10">
            <div className="flex justify-center mb-5">
              <span className="inline-flex items-center gap-1.5 bg-primary/10 text-primary text-xs font-bold px-3 py-1.5 rounded-full border border-primary/20 uppercase tracking-wider">
                <Sparkles className="h-3.5 w-3.5" />
                {textExclusive}
              </span>
            </div>

            <div className="text-center mb-6">
              <h2 className="text-2xl md:text-4xl font-extrabold tracking-tight text-foreground mb-2 leading-tight">
                {textWait} <br className="hidden sm:block" />
                <span className="text-primary">{textSave}</span>
              </h2>
              <p className="text-muted-foreground text-sm max-w-sm mx-auto">
                {textDesc}
              </p>
            </div>

            <div className="flex items-center justify-center gap-3 mb-6">
              <div className="flex items-center gap-2 bg-destructive/10 border border-destructive/20 text-destructive rounded-xl px-4 py-2.5">
                <Clock className="h-4 w-4 animate-pulse" />
                <span className="text-sm font-medium">{textExpires}</span>
                <span className="font-mono font-bold text-lg tabular-nums">{formatTime(timeLeft)}</span>
              </div>
            </div>

            <div
              onClick={handleCopyCode}
              role="button"
              tabIndex={0}
              onKeyDown={e => e.key === "Enter" && handleCopyCode()}
              className="cursor-pointer group relative flex items-center justify-between bg-muted rounded-2xl px-6 py-4 mb-6 border-2 border-dashed border-primary/40 hover:border-primary transition-colors"
            >
              <div className="flex items-center gap-3">
                <Tag className="h-5 w-5 text-primary" />
                <div>
                  <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider mb-0.5">{textPromo}</p>
                  <span className="font-mono font-extrabold text-2xl text-primary tracking-widest">UPLEVEL10</span>
                </div>
              </div>
              <div className={cn(
                "text-xs font-bold px-3 py-1.5 rounded-lg transition-all",
                copied
                  ? "bg-green-100 text-green-700 border border-green-300"
                  : "bg-primary/10 text-primary border border-primary/20 group-hover:bg-primary group-hover:text-primary-foreground"
              )}>
                {copied ? textCopied : textTapToCopy}
              </div>
            </div>

            <div className="flex flex-wrap justify-center gap-2 mb-7">
              {[
                { icon: <Shield className="h-3.5 w-3.5" />, text: textGuarantee },
                { icon: <Star className="h-3.5 w-3.5" />, text: textRated },
                { icon: <Sparkles className="h-3.5 w-3.5" />, text: textInsured },
              ].map(({ icon, text }) => (
                <span key={text} className="inline-flex items-center gap-1.5 text-xs text-muted-foreground bg-muted px-3 py-1.5 rounded-full border border-border font-medium">
                  {icon} {text}
                </span>
              ))}
            </div>

            <div className="space-y-3">
              <Button
                onClick={handleClaim}
                className="w-full h-12 text-base font-bold bg-primary text-primary-foreground hover:bg-primary/90 rounded-xl shadow-lg shadow-primary/25 btn-pulse group"
              >
                {textClaim}
                <ArrowRight className="h-4 w-4 ml-2 transition-transform group-hover:translate-x-1" />
              </Button>
              <button
                onClick={handleClose}
                className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors py-2 underline underline-offset-4"
              >
                {textNoThanks}
              </button>
            </div>
          </div>

          <div className="bg-muted/40 border-t border-border px-6 py-3 flex flex-wrap items-center justify-center gap-3 text-xs text-muted-foreground">
            <span>{textNoCC}</span>
            <span className="hidden sm:inline text-border">|</span>
            <span>{textCancel}</span>
            <span className="hidden sm:inline text-border">|</span>
            <span>{textNewCust}</span>
          </div>
        </div>
      </div>
    </div>
  );
};
